import { CanonicalActionUnavailableError } from './canonicalActionError';
import { useEffect, useRef, useState } from 'react';
import type { FlowActionEvent, FlowValues } from '../types';
import type { CanonicalFlowDriver } from './canonicalFlowDriver';

export interface CanonicalSessionCallbacks {
  beforeNavigate: (values: FlowValues, ratingComponentId?: string) => Promise<readonly string[]>;
  onAction?: (action: FlowActionEvent) => Promise<void> | void;
}

// Answers are plain data. Compare their contents so a repeated browser/widget
// notification does not publish a new session and trigger another render.
function sameAnswer(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) && Array.isArray(right) && left.length === right.length &&
      left.every((value, index) => sameAnswer(value, right[index]))
    );
  }
  if (
    Object.getPrototypeOf(left) !== Object.prototype ||
    Object.getPrototypeOf(right) !== Object.prototype
  ) return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every((key) =>
    Object.prototype.hasOwnProperty.call(right, key) && sameAnswer(left[key], right[key])
  );
}

/** Compiler validation precedes acknowledgement; failed saves retain answers and location. */
export function useCanonicalSurveySession(
  driver: CanonicalFlowDriver,
  initialValues: FlowValues,
  callbacks: CanonicalSessionCallbacks
) {
  const [state, setState] = useState(() => ({
    destination: driver.entry,
    visitId: 0,
    values: initialValues,
    hasAcknowledgedResponse: false,
    acknowledgedAnswers: structuredClone(driver.initialAcknowledgedAnswers ?? {}) as FlowValues,
    ratingComponentId: driver.initialRatingComponentId,
  }));
  const current = useRef(state);
  const currentDriver = useRef(driver);
  currentDriver.current = driver;
  // Media refresh replaces the driver; restore durable session acknowledgement
  // before render-time availability checks as well as event execution.
  if (state.hasAcknowledgedResponse) driver.acknowledgeResponse?.();
  const history = useRef([driver.entry]);
  const busy = useRef(false);
  const alive = useRef(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const active = (screenId: string) =>
    alive.current &&
    current.current.visitId === state.visitId &&
    !busy.current &&
    current.current.destination.kind === 'screen' &&
    current.current.destination.screenId === screenId;
  const setValue = (screenId: string, componentId: string, value: unknown) => {
    if (!active(screenId)) return;
    if (current.current.hasAcknowledgedResponse) currentDriver.current.acknowledgeResponse?.();
    let ratingComponentId;
    try {
      ratingComponentId = currentDriver.current.validateValue(
        screenId,
        componentId,
        current.current.values,
        current.current.ratingComponentId,
        { value }
      );
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'This survey component is no longer available.'
      );
      return;
    }
    if (
      ratingComponentId === current.current.ratingComponentId &&
      sameAnswer(current.current.values[componentId], value)
    ) {
      if (error !== null) setError(null);
      return;
    }
    const next = {
      ...current.current,
      values: { ...current.current.values, [componentId]: value },
      ratingComponentId,
    };
    current.current = next;
    setState(next);
    setError(null);
  };
  const interact = (
    screenId: string,
    componentId?: string,
    action?: FlowActionEvent,
    submitted?: { id: string; value: unknown },
    feedback?: { skip: boolean }
  ) => {
    if (!active(screenId)) return;
    if (current.current.hasAcknowledgedResponse) currentDriver.current.acknowledgeResponse?.();
    const previous = current.current;
    let result;
    try {
      const workingValues = feedback
        ? currentDriver.current.prepareFeedback?.(
            screenId,
            previous.values,
            previous.ratingComponentId,
            feedback.skip,
            previous.acknowledgedAnswers
          )
        : previous.values;
      if (!workingValues) throw new Error('This feedback step is unavailable.');
      if (submitted && submitted.id !== componentId)
        throw new Error('The answer does not belong to this component.');
      const preflight = currentDriver.current.guard(
        screenId,
        workingValues,
        previous.ratingComponentId,
        componentId,
        action
      );
      if (!preflight?.guardDenied && submitted)
        currentDriver.current.validateValue(
          screenId,
          submitted.id,
          workingValues,
          previous.ratingComponentId,
          { value: submitted.value, submission: true }
        );
      result =
        preflight ??
        currentDriver.current.resolve(
          screenId,
          submitted ? { ...workingValues, [submitted.id]: submitted.value } : workingValues,
          previous.ratingComponentId,
          componentId,
          action
        );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'This action is unavailable.');
      return;
    }
    busy.current = true;
    setPending(true);
    setError(null);
    void (async () => {
      let executingAction = false;
      try {
        const savedIds =
          result.editResponse || result.guardDenied || currentDriver.current.revisit
            ? []
            : await callbacks.beforeNavigate(result.values, result.ratingComponentId);
        if (!alive.current) return;
        if (feedback && !result.guardDenied)
          currentDriver.current.verifyFeedbackAcknowledgement?.(
            screenId,
            result.values,
            result.ratingComponentId,
            savedIds,
            previous.acknowledgedAnswers
          );
        const hasAcknowledgedResponse = previous.hasAcknowledgedResponse || savedIds.length > 0;
        if (hasAcknowledgedResponse) currentDriver.current.acknowledgeResponse?.();
        const ratingId = result.ratingComponentId;
        const answerIds = new Set(savedIds);
        const acknowledgedValues = result.editResponse
          ? result.values
          : Object.fromEntries(
              Object.entries(result.values).map(([id, value]) => [
                id,
                (value && typeof value === 'object' && 'submitted' in value) || answerIds.has(id)
                  ? {
                      ...(value as object),
                      submitted:
                        answerIds.has(id) ||
                        (previous.values[id] as { submitted?: boolean } | undefined)?.submitted ===
                          true,
                    }
                  : value,
              ])
            );
        // Summary data comes only from acknowledged persistence, never a widget's
        // submitted flag. Preserve the last acknowledged snapshot until explicit Edit.
        const acknowledgedAnswers = { ...previous.acknowledgedAnswers };
        if (result.editResponse && ratingId) delete acknowledgedAnswers[ratingId];
        else
          for (const id of savedIds) {
            if (Object.prototype.hasOwnProperty.call(acknowledgedValues, id))
              acknowledgedAnswers[id] = structuredClone(acknowledgedValues[id]);
          }
        // A successful save locks its owned answers even if a subsequent external action fails.
        current.current = {
          ...previous,
          values: acknowledgedValues,
          ratingComponentId: ratingId,
          hasAcknowledgedResponse,
          acknowledgedAnswers,
        };
        setState(current.current);
        executingAction = true;
        if (result.action)
          await callbacks.onAction?.({
            ...result.action,
            metadata: {
              ...result.action.metadata,
              values: acknowledgedValues,
              ratingComponentId: result.ratingComponentId,
            },
          });
        if (!alive.current) return;
        const next = {
          destination: result.destination,
          hasAcknowledgedResponse,
          acknowledgedAnswers,
          visitId: previous.visitId + 1,
          values: acknowledgedValues,
          ratingComponentId: result.ratingComponentId,
        };
        if (
          JSON.stringify(history.current[history.current.length - 1]) !==
          JSON.stringify(result.destination)
        )
          history.current.push(result.destination);
        current.current = next;
        setState(next);
      } catch (failure) {
        if (alive.current)
          setError(
            failure instanceof CanonicalActionUnavailableError
              ? failure.message
              : executingAction
                ? 'We could not complete this action. Your answers are still here. Please try again.'
                : 'We could not save your feedback. Your answers are still here. Please try again.'
          );
      } finally {
        busy.current = false;
        if (alive.current) setPending(false);
      }
    })();
  };
  const goBack = (screenId: string, componentId: string) => {
    if (!active(screenId)) return;
    if (current.current.hasAcknowledgedResponse) currentDriver.current.acknowledgeResponse?.();
    if (!currentDriver.current.usesHistoryBack(screenId, componentId)) {
      interact(screenId, componentId);
      return;
    }
    if (history.current.length < 2) return;
    try {
      currentDriver.current.validateValue(
        screenId,
        componentId,
        current.current.values,
        current.current.ratingComponentId
      );
    } catch {
      setError('This back action is no longer available.');
      return;
    }
    history.current = history.current.slice(0, -1);
    const next = {
      ...current.current,
      visitId: current.current.visitId + 1,
      destination: history.current[history.current.length - 1],
    };
    current.current = next;
    setState(next);
    setError(null);
  };
  const editSummary = (screenId: string) => {
    if (!active(screenId)) return;
    if (current.current.hasAcknowledgedResponse) currentDriver.current.acknowledgeResponse?.();
    const previous = current.current;
    const id = previous.ratingComponentId;
    if (!id || !Object.prototype.hasOwnProperty.call(previous.acknowledgedAnswers, id)) return;
    const result = currentDriver.current.summaryEdit(screenId, previous.values, id);
    if (!result) return;
    const acknowledgedAnswers = { ...previous.acknowledgedAnswers };
    delete acknowledgedAnswers[id];
    const next = {
      ...previous,
      destination: result.destination,
      values: result.values,
      acknowledgedAnswers,
      visitId: previous.visitId + 1,
    };
    history.current.push(result.destination);
    current.current = next;
    setState(next);
    setError(null);
  };
  return {
    ...state,
    pending,
    error,
    setValue,
    interact,
    goBack,
    editSummary,
    submitFeedback: (
      screenId: string,
      skip: boolean,
      componentId?: string,
      action?: FlowActionEvent
    ) => interact(screenId, componentId, action, undefined, { skip }),
    canGoBack: history.current.length > 1,
  };
}
